import { useState, useEffect } from "react";
import {
  listCustomers,
  listCustomerInternalAccounts,
  listPlatformInternalAccounts,
  listCustomerExternalAccounts,
  sandboxFundAccount,
  sandboxSend,
  transferIn,
} from "../services/gridApi";

export default function Simulator() {
  const [customers, setCustomers] = useState<any[]>([]);
  const [internalAccounts, setInternalAccounts] = useState<any[]>([]);

  // Fund state
  const [fundCustomerId, setFundCustomerId] = useState("");
  const [fundAccountId, setFundAccountId] = useState("");
  const [fundAmount, setFundAmount] = useState("");
  const [fundLoading, setFundLoading] = useState(false);
  const [fundResult, setFundResult] = useState<any>(null);
  const [fundError, setFundError] = useState("");

  // Transfer-In state
  const [tiCustomerId, setTiCustomerId] = useState("");
  const [tiExternalAccounts, setTiExternalAccounts] = useState<any[]>([]);
  const [tiInternalAccounts, setTiInternalAccounts] = useState<any[]>([]);
  const [tiSourceId, setTiSourceId] = useState("");
  const [tiDestId, setTiDestId] = useState("");
  const [tiAmount, setTiAmount] = useState("");
  const [tiLoading, setTiLoading] = useState(false);
  const [tiResult, setTiResult] = useState<any>(null);
  const [tiError, setTiError] = useState("");

  // Send state
  const [sendQuoteId, setSendQuoteId] = useState("");
  const [sendCurrency, setSendCurrency] = useState("USD");
  const [sendAmount, setSendAmount] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState<any>(null);
  const [sendError, setSendError] = useState("");

  // All Internal Accounts state
  const [allIaLoading, setAllIaLoading] = useState(false);
  const [allIaResult, setAllIaResult] = useState<any[] | null>(null);
  const [allIaError, setAllIaError] = useState("");

  useEffect(() => {
    listCustomers({ limit: 100 })
      .then((r) => setCustomers(r.data || []))
      .catch(() => {});
  }, []);

  // Fund Account: load internal accounts for selected customer
  useEffect(() => {
    if (!fundCustomerId) {
      setInternalAccounts([]);
      return;
    }
    Promise.all([
      listCustomerInternalAccounts({ customerId: fundCustomerId }),
      listPlatformInternalAccounts({}),
    ]).then(([custIa, platIa]) => {
      const custAccounts = (custIa.data || []).map((a: any) => ({ ...a, _label: "Customer" }));
      const platAccounts = (platIa.data || []).map((a: any) => ({ ...a, _label: "Platform" }));
      const custIds = new Set(custAccounts.map((a: any) => a.id));
      setInternalAccounts([...custAccounts, ...platAccounts.filter((a: any) => !custIds.has(a.id))]);
    }).catch(() => {});
  }, [fundCustomerId]);

  // Transfer-In: load accounts for selected customer
  useEffect(() => {
    if (!tiCustomerId) {
      setTiExternalAccounts([]);
      setTiInternalAccounts([]);
      return;
    }
    Promise.all([
      listCustomerExternalAccounts({ customerId: tiCustomerId }),
      listCustomerInternalAccounts({ customerId: tiCustomerId }),
      listPlatformInternalAccounts({}),
    ]).then(([ext, custIa, platIa]) => {
      setTiExternalAccounts(ext.data || []);
      const custAccounts = (custIa.data || []).map((a: any) => ({ ...a, _label: "Customer" }));
      const platAccounts = (platIa.data || []).map((a: any) => ({ ...a, _label: "Platform" }));
      const custIds = new Set(custAccounts.map((a: any) => a.id));
      setTiInternalAccounts([...custAccounts, ...platAccounts.filter((a: any) => !custIds.has(a.id))]);
    }).catch(() => {});
  }, [tiCustomerId]);

  const handleFund = async () => {
    setFundLoading(true);
    setFundError("");
    setFundResult(null);
    try {
      const amt = Math.round(parseFloat(fundAmount) * 100);
      const res = await sandboxFundAccount(fundAccountId, amt);
      setFundResult(res);
    } catch (e: any) {
      const detail = e.body ? JSON.stringify(e.body, null, 2) : "";
      setFundError(e.message + (detail && detail !== "{}" ? "\n" + detail : ""));
    } finally {
      setFundLoading(false);
    }
  };

  const handleTransferIn = async () => {
    setTiLoading(true);
    setTiError("");
    setTiResult(null);
    try {
      const res = await transferIn({
        source: { accountId: tiSourceId },
        destination: { accountId: tiDestId },
        amount: tiAmount ? Math.round(parseFloat(tiAmount) * 100) : undefined,
      });
      setTiResult(res);
    } catch (e: any) {
      const detail = e.body ? JSON.stringify(e.body, null, 2) : "";
      setTiError(e.message + (detail && detail !== "{}" ? "\n" + detail : ""));
    } finally {
      setTiLoading(false);
    }
  };

  const handleSend = async () => {
    setSendLoading(true);
    setSendError("");
    setSendResult(null);
    try {
      const res = await sandboxSend({
        quoteId: sendQuoteId,
        currencyCode: sendCurrency,
        currencyAmount: sendAmount ? Math.round(parseFloat(sendAmount) * 100) : undefined,
      });
      setSendResult(res);
    } catch (e: any) {
      const detail = e.body ? JSON.stringify(e.body, null, 2) : "";
      setSendError(e.message + (detail && detail !== "{}" ? "\n" + detail : ""));
    } finally {
      setSendLoading(false);
    }
  };

  const handleListAllIa = async () => {
    setAllIaLoading(true);
    setAllIaError("");
    setAllIaResult(null);
    try {
      const [custIa, platIa] = await Promise.all([
        listCustomerInternalAccounts({}),
        listPlatformInternalAccounts({}),
      ]);
      const all = [
        ...(custIa.data || []).map((a: any) => ({ ...a, _label: "Customer" })),
        ...(platIa.data || []).map((a: any) => ({ ...a, _label: "Platform" })),
      ];
      setAllIaResult(all);
    } catch (e: any) {
      const detail = e.body ? JSON.stringify(e.body, null, 2) : "";
      setAllIaError(e.message + (detail && detail !== "{}" ? "\n" + detail : ""));
    } finally {
      setAllIaLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight">Sandbox Simulator</h1>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded">
            Live API
          </span>
        </div>
        <p className="text-sm text-[#8A8A8A] mt-1">
          Execute real sandbox operations against the Grid API
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fund Account */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">Fund Account</h2>
          <p className="text-xs text-[#00B3E0] font-mono mb-4">POST /sandbox/internal-accounts/{"{id}"}/fund</p>
          {fundCustomerId && internalAccounts.length === 0 && (
            <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3 mb-3">
              <div className="text-xs text-yellow-400">No internal accounts found. Internal accounts are auto-created based on platform config. Check <span className="font-semibold">All Internal Accounts</span> below or try <span className="font-semibold">Transfer In</span> to pull funds from an external account.</div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-[#8A8A8A] mb-1">Customer</label>
              <select
                value={fundCustomerId}
                onChange={(e) => { setFundCustomerId(e.target.value); setFundAccountId(""); }}
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
              >
                <option value="">Select customer...</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.fullName || c.businessInfo?.legalName || c.platformCustomerId || c.id}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-[#8A8A8A] mb-1">Internal Account</label>
              <select
                value={fundAccountId}
                onChange={(e) => setFundAccountId(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
              >
                <option value="">Select account...</option>
                {internalAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a._label ? `[${a._label}] ` : ""}{a.balance?.currency?.code || "?"}: {((a.balance?.amount || 0) / 100).toFixed(2)} ({a.id.slice(0, 30)}...)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-[#8A8A8A] mb-1">Amount (dollars)</label>
              <input
                type="number"
                step="0.01"
                value={fundAmount}
                onChange={(e) => setFundAmount(e.target.value)}
                placeholder="100.00"
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm font-mono focus:border-[#00B3E0] focus:outline-none"
              />
            </div>

            <button
              onClick={handleFund}
              disabled={fundLoading || !fundAccountId || !fundAmount}
              className="w-full bg-emerald-500 text-black font-semibold py-2 rounded-lg hover:bg-emerald-400 disabled:opacity-50"
            >
              {fundLoading ? "Funding..." : "Fund Account"}
            </button>

            {fundError && <pre className="text-sm text-red-400 whitespace-pre-wrap">{fundError}</pre>}
            {fundResult && (
              <pre className="text-xs font-mono text-emerald-400 bg-[#0A0A0A] p-3 rounded-lg overflow-auto max-h-40">
                {JSON.stringify(fundResult, null, 2)}
              </pre>
            )}
          </div>
        </div>

        {/* Transfer In */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">Transfer In</h2>
          <p className="text-xs text-[#00B3E0] font-mono mb-4">POST /transfer-in</p>
          <p className="text-xs text-[#8A8A8A] mb-3">Pull funds from an external account into an internal account. In sandbox, transfers complete instantly.</p>

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-[#8A8A8A] mb-1">Customer</label>
              <select
                value={tiCustomerId}
                onChange={(e) => { setTiCustomerId(e.target.value); setTiSourceId(""); setTiDestId(""); }}
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
              >
                <option value="">Select customer...</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.fullName || c.businessInfo?.legalName || c.platformCustomerId || c.id}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-[#8A8A8A] mb-1">Source (External Account)</label>
              <select
                value={tiSourceId}
                onChange={(e) => setTiSourceId(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
              >
                <option value="">Select external account...</option>
                {tiExternalAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.currency || "?"} - {a.accountInfo?.accountType || a.accountInfo?.pixKeyType || "account"} ({a.id.slice(0, 30)}...)
                  </option>
                ))}
              </select>
              {tiCustomerId && tiExternalAccounts.length === 0 && (
                <p className="text-xs text-yellow-400 mt-1">No external accounts. Create one on the customer detail page first.</p>
              )}
            </div>

            <div>
              <label className="block text-xs text-[#8A8A8A] mb-1">Destination (Internal Account)</label>
              <select
                value={tiDestId}
                onChange={(e) => setTiDestId(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
              >
                <option value="">Select internal account...</option>
                {tiInternalAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a._label ? `[${a._label}] ` : ""}{a.balance?.currency?.code || "?"}: {((a.balance?.amount || 0) / 100).toFixed(2)} ({a.id.slice(0, 30)}...)
                  </option>
                ))}
              </select>
              {tiCustomerId && tiInternalAccounts.length === 0 && (
                <p className="text-xs text-yellow-400 mt-1">No internal accounts found. They should be auto-created by the platform.</p>
              )}
            </div>

            <div>
              <label className="block text-xs text-[#8A8A8A] mb-1">Amount (dollars, optional)</label>
              <input
                type="number"
                step="0.01"
                value={tiAmount}
                onChange={(e) => setTiAmount(e.target.value)}
                placeholder="100.00"
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm font-mono focus:border-[#00B3E0] focus:outline-none"
              />
            </div>

            <button
              onClick={handleTransferIn}
              disabled={tiLoading || !tiSourceId || !tiDestId}
              className="w-full bg-blue-500 text-white font-semibold py-2 rounded-lg hover:bg-blue-400 disabled:opacity-50"
            >
              {tiLoading ? "Transferring..." : "Transfer In"}
            </button>

            {tiError && <pre className="text-sm text-red-400 whitespace-pre-wrap">{tiError}</pre>}
            {tiResult && (
              <pre className="text-xs font-mono text-blue-400 bg-[#0A0A0A] p-3 rounded-lg overflow-auto max-h-40">
                {JSON.stringify(tiResult, null, 2)}
              </pre>
            )}
          </div>
        </div>

        {/* Simulate Send */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">Simulate Send</h2>
          <p className="text-xs text-[#00B3E0] font-mono mb-4">POST /sandbox/send</p>
          <p className="text-xs text-[#8A8A8A] mb-3">Simulate a bank payment for a quote. Create a quote on the Transfer page first, then paste the Quote ID here.</p>

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-[#8A8A8A] mb-1">Quote ID</label>
              <input
                type="text"
                value={sendQuoteId}
                onChange={(e) => setSendQuoteId(e.target.value)}
                placeholder="Quote:01234567-..."
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm font-mono focus:border-[#00B3E0] focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#8A8A8A] mb-1">Currency</label>
                <select
                  value={sendCurrency}
                  onChange={(e) => setSendCurrency(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                >
                  {["USD", "EUR", "GBP", "BRL", "MXN", "BTC", "USDC"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#8A8A8A] mb-1">Amount (optional)</label>
                <input
                  type="number"
                  step="0.01"
                  value={sendAmount}
                  onChange={(e) => setSendAmount(e.target.value)}
                  placeholder="Derived from quote"
                  className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2 text-[#F8F8F7] text-sm font-mono focus:border-[#00B3E0] focus:outline-none"
                />
              </div>
            </div>

            <button
              onClick={handleSend}
              disabled={sendLoading || !sendQuoteId}
              className="w-full bg-purple-500 text-white font-semibold py-2 rounded-lg hover:bg-purple-400 disabled:opacity-50"
            >
              {sendLoading ? "Sending..." : "Simulate Send"}
            </button>

            {sendError && <pre className="text-sm text-red-400 whitespace-pre-wrap">{sendError}</pre>}
            {sendResult && (
              <pre className="text-xs font-mono text-purple-400 bg-[#0A0A0A] p-3 rounded-lg overflow-auto max-h-40">
                {JSON.stringify(sendResult, null, 2)}
              </pre>
            )}
          </div>
        </div>

        {/* All Internal Accounts */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">All Internal Accounts</h2>
          <p className="text-xs text-[#8A8A8A] mb-4">
            List all internal accounts across the platform. Internal accounts should be auto-created when customers are created.
          </p>

          <button
            onClick={handleListAllIa}
            disabled={allIaLoading}
            className="w-full bg-[#333333] text-[#F8F8F7] font-semibold py-2 rounded-lg hover:bg-[#444444] disabled:opacity-50 mb-3"
          >
            {allIaLoading ? "Loading..." : "List All Internal Accounts"}
          </button>

          {allIaError && <pre className="text-sm text-red-400 whitespace-pre-wrap">{allIaError}</pre>}
          {allIaResult !== null && allIaResult.length === 0 && (
            <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3">
              <div className="text-xs text-yellow-400">No internal accounts found on the entire platform. This may be a platform configuration issue. Contact <a href="mailto:support@lightspark.com" className="underline">support@lightspark.com</a> to verify your sandbox setup.</div>
            </div>
          )}
          {allIaResult !== null && allIaResult.length > 0 && (
            <div className="space-y-2">
              {allIaResult.map((a: any) => (
                <div key={a.id} className="bg-[#0A0A0A] rounded-lg p-3 border border-[#222222]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-[#F8F8F7]">{a._label || "Account"}</span>
                    <span className="text-xs font-mono text-emerald-400">{a.balance?.currency?.code || "?"}: {((a.balance?.amount || 0) / 100).toFixed(2)}</span>
                  </div>
                  <div className="text-[10px] font-mono text-[#8A8A8A] truncate">{a.id}</div>
                  {a.customerId && <div className="text-[10px] font-mono text-[#8A8A8A] truncate">Customer: {a.customerId}</div>}
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 border-t border-[#222222] pt-4">
            <h3 className="text-sm font-semibold text-[#F8F8F7] mb-2">Sandbox Test Patterns</h3>
            <p className="text-xs text-[#8A8A8A] mb-3">
              Use these account number endings when creating external accounts:
            </p>
            <div className="space-y-1">
              {[
                { ending: "002", desc: "Insufficient funds", color: "text-red-400" },
                { ending: "003", desc: "Account closed / invalid", color: "text-red-400" },
                { ending: "004", desc: "Transfer rejected", color: "text-orange-400" },
                { ending: "005", desc: "Timeout (~30s, then fails)", color: "text-yellow-400" },
                { ending: "Other", desc: "Success", color: "text-emerald-400" },
              ].map((p) => (
                <div key={p.ending} className="flex items-center justify-between p-1.5 rounded bg-white/[0.02]">
                  <code className="text-xs font-mono text-[#F8F8F7]">...{p.ending}</code>
                  <span className={`text-[10px] ${p.color}`}>{p.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
